import type { BendAngleDeg, Uid } from './types.js';

/**
 * Estado editable de un pin dentro de una instancia de componente.
 * La geometría original (longitud, número de puntos de doblez, etc.)
 * vive en la ComponentDef; aquí solo se guarda lo que el usuario
 * ha modificado: los ángulos de cada doblez y el punto de corte.
 */

/**
 * Estado de un punto de doblez.
 * Cada componente define N puntos por pin, repartidos uniformes y centrados.
 * En cada punto, el pin puede acumular dos ángulos independientes:
 *
 *  - angle_in_plane_deg:   giro dentro del plano de la pantalla.
 *                          + = derecha (según orientación local del pin),
 *                          - = izquierda.
 *  - angle_out_of_plane_deg: giro perpendicular al plano.
 *                          + = arriba (hacia el usuario, saliendo de la pantalla),
 *                          - = abajo (entrando en la pantalla).
 *
 * Ambos son múltiplos de 45°. Se aplican como rotación al RESTO del pin
 * (desde este punto hasta el extremo).
 *
 * Un mismo pin puede combinar dobleces en plano y fuera de plano en
 * distintos puntos, describiendo arcos que entran y salen del plano.
 */
export interface BendState {
  angle_in_plane_deg: BendAngleDeg;
  angle_out_of_plane_deg: BendAngleDeg;
}

/** Un pin sin dobleces ni cortes se crea con este estado. */
export function makeStraightBend(): BendState {
  return { angle_in_plane_deg: 0, angle_out_of_plane_deg: 0 };
}

export interface PinState {
  /** Referencia al PinDef.id de la definición del componente. */
  pin_id: string;
  /**
   * Estado de cada punto de doblez, alineado con el índice del punto.
   * Longitud = ComponentDef.pins[i].bend_points_count.
   */
  bends: BendState[];
  /**
   * Índice del punto de doblez donde el usuario cortó el pin.
   * null = pin entero.
   * Si tiene valor, todo lo que va desde ese punto hasta el extremo
   * original se considera eliminado, y los dobleces con índice > cut
   * quedan inutilizados (no se renderizan).
   */
  cut_at_index: number | null;
  /**
   * Ajuste de longitud del ÚLTIMO segmento del pin (mm). Se usa para
   * que el tip caiga exactamente en un nodo de la cuadrícula tras un
   * doblez/corte. Puede ser negativo.
   */
  length_adjust_mm?: number;
  /**
   * Ajuste de ángulo aplicado a un bend concreto (grados). Se SUMA al
   * `angle_in_plane_deg` de ese bend en el momento del render. Junto
   * con `length_adjust_mm` sirve para llevar el tip a un nodo de
   * rejilla exacto sin generar una doblez extra en el extremo.
   *
   * Debe usarse junto con `angle_adjust_at_bend` (índice del bend
   * afectado). Sin `angle_adjust_at_bend`, se ignora.
   */
  angle_adjust_deg?: number;
  angle_adjust_at_bend?: number;
  /**
   * Correcciones acumuladas por bend para RE-ALINEAR ese bend a un
   * nodo de rejilla en el momento en el que el usuario lo dobla.
   * Mapa bendIndex → delta grados que se SUMA al `angle_in_plane_deg`
   * de ese bend en el render. Independiente de la corrección
   * `angle_adjust_*` (que sólo sirve para el snap del tip).
   */
  align_offsets?: Record<number, number>;
}

/**
 * Crea el estado inicial de un pin. Sin `initialBends`, todos los dobleces
 * a 0 y sin cortes. Con `initialBends`, cada punto arranca con esos
 * ángulos (útil para pines pre-conformados como los de un DIP).
 */
export function makeInitialPinState(
  pinId: string,
  bendPointsCount: number,
  initialBends?: readonly BendState[],
): PinState {
  const bends: BendState[] = initialBends
    ? Array.from({ length: bendPointsCount }, (_, i) => ({
        angle_in_plane_deg: initialBends[i]?.angle_in_plane_deg ?? 0,
        angle_out_of_plane_deg: initialBends[i]?.angle_out_of_plane_deg ?? 0,
      }))
    : Array.from({ length: bendPointsCount }, makeStraightBend);
  return { pin_id: pinId, bends, cut_at_index: null };
}

/**
 * Ancla de una soldadura: identifica un punto concreto de un pin donde
 * se puede colocar una bola de soldadura.
 *  - "tip"          → extremo final del pin (o del pin cortado).
 *  - "bend:N"       → punto de doblez con índice N.
 * NUNCA el extremo pegado al cuerpo del componente.
 */
export type PinAnchor = 'tip' | `bend:${number}`;

export interface PinRef {
  component_uid: Uid;
  pin_id: string;
  anchor: PinAnchor;
}
